/**
 * Hand-rolled validator for `ExportBundle` import (Requirements 4.9,
 * 12.5, Property 22).
 *
 * The validator is deliberately external and zero-dependency: the
 * project budget keeps the production bundle under 350 KB gzipped
 * (Requirement 11.4) and import is a settings-only action where a
 * tiny, fast type check beats pulling in `zod`.
 *
 * Validation walks the input top-down and rejects on the first bad
 * path with a stable, human-readable error path like
 * `threads[3].messages[7].role`. Both the Dexie-backed and in-memory
 * adapters import this module so the rejection contract is identical
 * regardless of which adapter is active.
 *
 * Pure: no globals, no I/O, no exceptions thrown to callers.
 */

import type {
  ConversationThread,
  ExportBundle,
  Message,
  MessageRole,
  MessageStatus,
  PacingIntensity,
  Preferences,
  PracticeMode,
  ProfileContext,
  ReducedMotionOverride,
} from '../../domain/types';

/** Discriminated result of a single validation walk. */
export type ValidateResult =
  | { ok: true; bundle: ExportBundle }
  | { ok: false; errorPath: string; reason: string };

const MESSAGE_ROLES: readonly MessageRole[] = ['user', 'assistant'];
const MESSAGE_STATUSES: readonly MessageStatus[] = [
  'queued',
  'sent',
  'streaming',
  'complete',
  'failed',
  'softFallback',
];
const LANGUAGE_CODES = ['tr', 'en', 'el'] as const;
const PRACTICE_MODES: readonly PracticeMode[] = ['off', 'en', 'el'];
const PACING_INTENSITIES: readonly PacingIntensity[] = [
  'subtle',
  'balanced',
  'warm',
];
const REDUCED_MOTION_OVERRIDES: readonly ReducedMotionOverride[] = [
  'system',
  'on',
  'off',
];

/** Internal record shape used throughout the validator. */
type Rec = Record<string, unknown>;

function isRecord(x: unknown): x is Rec {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}

function fail(errorPath: string, reason: string): ValidateResult {
  return { ok: false, errorPath, reason };
}

function checkString(
  value: unknown,
  path: string,
): ValidateResult | { ok: true; value: string } {
  if (typeof value !== 'string') {
    return fail(path, `expected string, got ${typeof value}`);
  }
  return { ok: true, value };
}

function checkNumber(
  value: unknown,
  path: string,
): ValidateResult | { ok: true; value: number } {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return fail(path, `expected number, got ${typeof value}`);
  }
  return { ok: true, value };
}

function checkBoolean(
  value: unknown,
  path: string,
): ValidateResult | { ok: true; value: boolean } {
  if (typeof value !== 'boolean') {
    return fail(path, `expected boolean, got ${typeof value}`);
  }
  return { ok: true, value };
}

function checkStringArray(
  value: unknown,
  path: string,
): ValidateResult | { ok: true; value: string[] } {
  if (!Array.isArray(value)) {
    return fail(path, `expected array, got ${typeof value}`);
  }
  for (let i = 0; i < value.length; i += 1) {
    if (typeof value[i] !== 'string') {
      return fail(`${path}[${i}]`, `expected string, got ${typeof value[i]}`);
    }
  }
  return { ok: true, value: value as string[] };
}

function validateProfile(
  raw: unknown,
  path: string,
): ValidateResult | { ok: true; value: ProfileContext } {
  if (!isRecord(raw)) {
    return fail(path, `expected object, got ${typeof raw}`);
  }
  if (raw['schemaVersion'] !== 1) {
    return fail(`${path}.schemaVersion`, 'expected literal 1');
  }
  const stringFields: ReadonlyArray<keyof ProfileContext> = [
    'displayName',
    'dateOfBirth',
    'countryOfOrigin',
    'countryOfResidence',
    'hairDescription',
    'favoriteFood',
    'accessory',
    'fandom',
    'favoriteColor',
    'notes',
  ];
  for (const field of stringFields) {
    const r = checkString(raw[field], `${path}.${String(field)}`);
    if ('ok' in r && r.ok === false) return r;
  }
  for (const field of ['languagesLearning', 'hobbies', 'interests'] as const) {
    const r = checkStringArray(raw[field], `${path}.${field}`);
    if ('ok' in r && r.ok === false) return r;
  }
  const pet = raw['pet'];
  if (!isRecord(pet)) {
    return fail(`${path}.pet`, `expected object, got ${typeof pet}`);
  }
  for (const field of ['species', 'name'] as const) {
    const r = checkString(pet[field], `${path}.pet.${field}`);
    if ('ok' in r && r.ok === false) return r;
  }
  return { ok: true, value: raw as unknown as ProfileContext };
}

function validatePreferences(
  raw: unknown,
  path: string,
): ValidateResult | { ok: true; value: Preferences } {
  if (!isRecord(raw)) {
    return fail(path, `expected object, got ${typeof raw}`);
  }
  if (raw['schemaVersion'] !== 1) {
    return fail(`${path}.schemaVersion`, 'expected literal 1');
  }
  const language = raw['language'];
  if (
    typeof language !== 'string' ||
    !LANGUAGE_CODES.includes(language as (typeof LANGUAGE_CODES)[number])
  ) {
    return fail(`${path}.language`, 'expected one of tr | en | el');
  }
  const practice = raw['practiceMode'];
  if (typeof practice !== 'string' || !PRACTICE_MODES.includes(practice as PracticeMode)) {
    return fail(`${path}.practiceMode`, 'expected one of off | en | el');
  }
  const themeId = checkString(raw['activeThemeId'], `${path}.activeThemeId`);
  if ('ok' in themeId && themeId.ok === false) return themeId;
  const pacing = raw['pacing'];
  if (typeof pacing !== 'string' || !PACING_INTENSITIES.includes(pacing as PacingIntensity)) {
    return fail(`${path}.pacing`, 'expected one of subtle | balanced | warm');
  }
  const motion = raw['reducedMotionOverride'];
  if (
    typeof motion !== 'string' ||
    !REDUCED_MOTION_OVERRIDES.includes(motion as ReducedMotionOverride)
  ) {
    return fail(
      `${path}.reducedMotionOverride`,
      'expected one of system | on | off',
    );
  }
  const budget = checkNumber(raw['tokenBudget'], `${path}.tokenBudget`);
  if ('ok' in budget && budget.ok === false) return budget;
  const forcedModel = raw['forcedModel'];
  if (forcedModel !== null && typeof forcedModel !== 'string') {
    return fail(`${path}.forcedModel`, 'expected string or null');
  }
  return { ok: true, value: raw as unknown as Preferences };
}

function validateMessage(
  raw: unknown,
  path: string,
): ValidateResult | { ok: true; value: Message } {
  if (!isRecord(raw)) {
    return fail(path, `expected object, got ${typeof raw}`);
  }
  for (const field of ['id', 'threadId', 'content'] as const) {
    const r = checkString(raw[field], `${path}.${field}`);
    if ('ok' in r && r.ok === false) return r;
  }
  const role = raw['role'];
  if (typeof role !== 'string' || !MESSAGE_ROLES.includes(role as MessageRole)) {
    return fail(`${path}.role`, 'expected user | assistant');
  }
  const createdAt = checkNumber(raw['createdAt'], `${path}.createdAt`);
  if ('ok' in createdAt && createdAt.ok === false) return createdAt;
  const status = raw['status'];
  if (
    typeof status !== 'string' ||
    !MESSAGE_STATUSES.includes(status as MessageStatus)
  ) {
    return fail(
      `${path}.status`,
      'expected one of queued | sent | streaming | complete | failed | softFallback',
    );
  }
  const summarized = checkBoolean(raw['summarized'], `${path}.summarized`);
  if ('ok' in summarized && summarized.ok === false) return summarized;
  // Optional fields: modelUsed (string), language (LanguageCode).
  if ('modelUsed' in raw && raw['modelUsed'] !== undefined) {
    const r = checkString(raw['modelUsed'], `${path}.modelUsed`);
    if ('ok' in r && r.ok === false) return r;
  }
  if ('language' in raw && raw['language'] !== undefined) {
    const language = raw['language'];
    if (
      typeof language !== 'string' ||
      !LANGUAGE_CODES.includes(language as (typeof LANGUAGE_CODES)[number])
    ) {
      return fail(`${path}.language`, 'expected one of tr | en | el');
    }
  }
  return { ok: true, value: raw as unknown as Message };
}

function validateThread(
  raw: unknown,
  path: string,
): ValidateResult | { ok: true; value: ConversationThread } {
  if (!isRecord(raw)) {
    return fail(path, `expected object, got ${typeof raw}`);
  }
  for (const field of ['id', 'title'] as const) {
    const r = checkString(raw[field], `${path}.${field}`);
    if ('ok' in r && r.ok === false) return r;
  }
  for (const field of ['createdAt', 'updatedAt'] as const) {
    const r = checkNumber(raw[field], `${path}.${field}`);
    if ('ok' in r && r.ok === false) return r;
  }
  const messages = raw['messages'];
  if (!Array.isArray(messages)) {
    return fail(`${path}.messages`, `expected array, got ${typeof messages}`);
  }
  for (let i = 0; i < messages.length; i += 1) {
    const r = validateMessage(messages[i], `${path}.messages[${i}]`);
    if ('ok' in r && r.ok === false) return r;
  }
  const summary = raw['rollingSummary'];
  if (summary !== null && typeof summary !== 'string') {
    return fail(`${path}.rollingSummary`, 'expected string or null');
  }
  const through = raw['summarizedThroughMessageId'];
  if (through !== null && typeof through !== 'string') {
    return fail(
      `${path}.summarizedThroughMessageId`,
      'expected string or null',
    );
  }
  return { ok: true, value: raw as unknown as ConversationThread };
}

/**
 * Validate an unknown value as an `ExportBundle`.
 *
 * Returns `{ ok: true, bundle }` on success. On the first bad field
 * the function returns `{ ok: false, errorPath, reason }` and stops
 * walking; this is what `importAll` uses to leave the underlying
 * tables byte-identical to their pre-call state (Property 22).
 */
export function validateExportBundle(bundle: unknown): ValidateResult {
  if (!isRecord(bundle)) {
    return fail('$', `expected object, got ${typeof bundle}`);
  }
  if (bundle['schemaVersion'] !== 1) {
    return fail('schemaVersion', 'expected literal 1');
  }
  const exportedAt = checkNumber(bundle['exportedAt'], 'exportedAt');
  if ('ok' in exportedAt && exportedAt.ok === false) return exportedAt;
  const profile = validateProfile(bundle['profile'], 'profile');
  if ('ok' in profile && profile.ok === false) return profile;
  const preferences = validatePreferences(bundle['preferences'], 'preferences');
  if ('ok' in preferences && preferences.ok === false) return preferences;
  const companionName = bundle['companionName'];
  if (companionName !== null && typeof companionName !== 'string') {
    return fail('companionName', 'expected string or null');
  }
  const threads = bundle['threads'];
  if (!Array.isArray(threads)) {
    return fail('threads', `expected array, got ${typeof threads}`);
  }
  for (let i = 0; i < threads.length; i += 1) {
    const r = validateThread(threads[i], `threads[${i}]`);
    if ('ok' in r && r.ok === false) return r;
  }
  return { ok: true, bundle: bundle as unknown as ExportBundle };
}
